import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { seoConfig } from '../utils/seoConfig';

const SEOHead = ({ 
  title, 
  description, 
  keywords, 
  image, 
  type = 'website',
  schema,
  publishedTime,
  modifiedTime,
  author,
  canonicalUrl,
  noindex = false,
  nofollow = false
}) => {
  const location = useLocation();
  const currentPath = location.pathname;
  
  // Get page-specific SEO config
  const getPageConfig = () => {
    switch (currentPath) {
      case '/':
        return seoConfig.pages.home;
      case '/about-us':
        return seoConfig.pages.about;
      case '/services':
        return seoConfig.pages.services;
      case '/case-studies':
        return seoConfig.pages.caseStudies;
      case '/testimonials':
        return seoConfig.pages.testimonials;
      case '/contact':
        return seoConfig.pages.contact;
      default:
        return seoConfig.pages.home;
    }
  };

  const pageConfig = getPageConfig();
  
  // Use provided props or fall back to page config
  const finalTitle = title || pageConfig.title || seoConfig.defaultTitle;
  const finalDescription = description || pageConfig.description || seoConfig.defaultDescription;
  const finalKeywords = keywords || pageConfig.keywords || '';
  const finalImage = image || pageConfig.image || seoConfig.defaultImage;
  const finalType = type || pageConfig.type || 'website';
  const finalCanonicalUrl = canonicalUrl || `${seoConfig.baseUrl}${currentPath}`;
  
  // Create robots meta content
  const robotsContent = `${noindex ? 'noindex' : 'index'}, ${nofollow ? 'nofollow' : 'follow'}, max-image-preview:large, max-snippet:-1, max-video-preview:-1`;

  // Generate breadcrumb schema
  const generateBreadcrumbSchema = () => {
    const pathSegments = currentPath.split('/').filter(segment => segment);
    const breadcrumbItems = [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": seoConfig.baseUrl
      }
    ];

    let currentUrl = seoConfig.baseUrl;
    pathSegments.forEach((segment, index) => {
      currentUrl += `/${segment}`;
      const name = segment.charAt(0).toUpperCase() + segment.slice(1).replace('-', ' ');
      breadcrumbItems.push({
        "@type": "ListItem",
        "position": index + 2,
        "name": name,
        "item": currentUrl
      });
    });

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbItems
    };
  };

  // Generate page-specific schema
  const generatePageSchema = () => {
    const baseSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${finalCanonicalUrl}#webpage`,
      "url": finalCanonicalUrl,
      "name": finalTitle,
      "description": finalDescription,
      "isPartOf": {
        "@id": `${seoConfig.baseUrl}/#website`
      },
      "about": {
        "@id": `${seoConfig.baseUrl}/#organization`
      },
      "datePublished": publishedTime || "2020-01-01T00:00:00+05:30",
      "dateModified": modifiedTime || new Date().toISOString(),
      "inLanguage": "en-IN",
      "potentialAction": [
        {
          "@type": "ReadAction",
          "target": [finalCanonicalUrl]
        }
      ]
    };

    // Add author if provided
    if (author) {
      baseSchema.author = {
        "@type": "Person",
        "name": author
      };
    }

    // Merge with page-specific schema
    if (schema) {
      return { ...baseSchema, ...schema };
    }

    return baseSchema;
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      {finalKeywords && <meta name="keywords" content={finalKeywords} />}
      <meta name="author" content={author || "QuantumFrame Studio"} />
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      <meta name="bingbot" content="index, follow" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={finalCanonicalUrl} />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:type" content={finalType} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={finalCanonicalUrl} />
      <meta property="og:image" content={`${seoConfig.baseUrl}${finalImage}`} />
      <meta property="og:image:secure_url" content={`${seoConfig.baseUrl}${finalImage}`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={finalTitle} />
      <meta property="og:site_name" content={seoConfig.siteName} />
      <meta property="og:locale" content="en_IN" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@quantumframest" />
      <meta name="twitter:creator" content="@quantumframest" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={`${seoConfig.baseUrl}${finalImage}`} />
      <meta name="twitter:image:alt" content={finalTitle} />
      
      {/* Article Meta Tags (if applicable) */}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {author && <meta property="article:author" content={author} />}
      
      {/* LinkedIn Meta Tags */}
      <meta property="og:site_name" content={seoConfig.siteName} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#12193C" />
      <meta name="msapplication-TileColor" content="#12193C" />
      <meta name="apple-mobile-web-app-title" content={seoConfig.siteName} />
      <meta name="application-name" content={seoConfig.siteName} />
      
      {/* Geo Meta Tags */}
      <meta name="geo.region" content="IN-MP" />
      <meta name="geo.placename" content="Bhopal, Madhya Pradesh" />
      <meta name="geo.position" content="23.2599;77.4126" />
      <meta name="ICBM" content="23.2599, 77.4126" />
      
      {/* Business Meta Tags */}
      <meta name="contact" content="info@quantumframestudio.com" />
      <meta name="reply-to" content="info@quantumframestudio.com" />
      <meta name="owner" content="QuantumFrame Studio" />
      <meta name="url" content={finalCanonicalUrl} />
      <meta name="identifier-URL" content={finalCanonicalUrl} />
      <meta name="directory" content="submission" />
      <meta name="category" content="Web Development, Digital Marketing, Technology" />
      <meta name="coverage" content="Worldwide" />
      <meta name="distribution" content="Global" />
      <meta name="rating" content="General" />
      <meta name="revisit-after" content="7 days" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(generatePageSchema())}
      </script>
      
      {/* Breadcrumb Schema */}
      {currentPath !== '/' && (
        <script type="application/ld+json">
          {JSON.stringify(generateBreadcrumbSchema())}
        </script>
      )}
      
      {/* Page-specific Schema */}
      {pageConfig.schema && (
        <script type="application/ld+json">
          {JSON.stringify(pageConfig.schema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;